/** 时间表的日 / 周 / 月视图（与 iOS ScheduleView 的 DayAgenda / WeekAgenda / MonthAgenda 一致） */
import { Bell, BookOpen, CalendarDays, ChevronLeft, ChevronRight, CircleCheck, ExternalLink } from "lucide-react";
import { useEffect, useRef, useState, type TouchEvent } from "react";
import {
  dayPart,
  dayText,
  fetchAgenda,
  isAllDay,
  isDone,
  isMultiDay,
  isTimedEvent,
  timeModeOf,
  offsetText,
  select,
  shallowEqual,
  shortTimeText,
  timeLines,
  timeText,
  useCalendar,
  useStore,
  WEEKDAY_NAMES,
  agendaItemId,
  checkinKeyOf,
  type Checkin,
  type AgendaItem,
  type Child,
  type LocalCalendar,
  type ReminderRule,
} from "../../core";
import { CategoryLegend, categoryStyle, EmptyHint, KPIcon, Pill, C } from "../../ui";
import { describeRepeat, parseRepeat } from "./repeat";

/** 打开编辑器：eventId 为空时新建 */
export type OpenEditor = (eventId: string | null, date: string) => void;

/** 日程打开什么的说明："学习" / "打开 喜马拉雅"，只提醒为 null */
export function actionText(it: Pick<AgendaItem, "action">): string | null {
  if (it.action?.type === "content") return "学习";
  if (it.action?.type === "app") return `打开 ${it.action.name}`;
  return null;
}

// ---------- 数据 ----------

/** 拉取一段日期的日程展开；每次同步到新数据（seq 变化）再拉 */
function useAgendaRange(childId: string, from: string, to: string): void {
  const seq = useStore((s) => s.seq);
  useEffect(() => {
    void fetchAgenda(childId, from, to);
  }, [childId, from, to, seq]);
}

/** 每分钟刷新一次的当前时间（"进行中"高亮用） */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function useDays(childId: string, dates: string[]): AgendaItem[][] {
  const days = useStore((s) => dates.map((d) => select.agendaDay(s, childId, d)), shallowEqual);
  return days.map((d) => d?.items ?? []);
}

/** 日程的提醒规则（启用的） */
function ruleOf(rules: Record<string, ReminderRule>, eventId: string): ReminderRule | undefined {
  return Object.values(rules).find((r) => r.sourceType === "event" && r.sourceId === eventId && r.enabled);
}

function weekDates(cal: LocalCalendar, monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => cal.addDays(monday, i));
}

const dayNum = (d: string) => Number(d.slice(-2));

// ---------- 日视图 ----------

export function DayView({ child, date, onSelect, onEdit }: { child: Child; date: string; onSelect: (d: string) => void; onEdit: OpenEditor }) {
  const cal = useCalendar();
  const monday = cal.mondayOf(date);
  useAgendaRange(child.id, monday, cal.addDays(monday, 6));
  const items = useStore((s) => select.agendaDay(s, child.id, date)?.items);
  const loaded = items != null;
  const list = items ?? [];
  const now = useNow();
  const today = cal.today();

  return (
    <div className="sch-day">
      <div className="sch-week-nav is-desktop">
        <button type="button" className="kp-icon-btn" aria-label="上一周" title="上一周" onClick={() => onSelect(cal.addDays(date, -7))}>
          <ChevronLeft size={18} strokeWidth={2.6} />
        </button>
        <span className="sch-range">
          {cal.monthDay(monday)} – {cal.monthDay(cal.addDays(monday, 6))}
        </span>
        <button type="button" className="kp-icon-btn" aria-label="下一周" title="下一周" onClick={() => onSelect(cal.addDays(date, 7))}>
          <ChevronRight size={18} strokeWidth={2.6} />
        </button>
        {cal.mondayOf(today) !== monday || date !== today ? (
          <button type="button" className="sch-today-btn" onClick={() => onSelect(today)}>
            今天
          </button>
        ) : null}
      </div>
      <WeekStrip monday={monday} selected={date} onSelect={onSelect} onPage={(n) => onSelect(cal.addDays(date, n * 7))} />
      <div className="sch-day-title">
        <span>
          {cal.relativeDay(date) === cal.monthDay(date) ? cal.monthDay(date) : `${cal.relativeDay(date)} · ${cal.monthDay(date)}`} {cal.weekdayName(date)}
        </span>
        {list.length > 0 && <span className="sch-count">{list.length} 项</span>}
      </div>
      <div className="sch-list">
        {loaded && list.length === 0 && <EmptyHint icon={CalendarDays} text={`${cal.monthDay(date)} 没有安排`} />}
        {!loaded && <div className="sch-loading">加载中…</div>}
        {list.map((it) => {
          const isNow = isTimedEvent(it) && it.startAt <= now && now < it.endAt;
          return (
            <button key={agendaItemId(it)} type="button" className="sch-row-wrap is-button" onClick={() => onEdit(it.eventId, date)}>
              <AgendaRow item={it} isNow={isNow} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** 周一到周日的日期条：周末红色，今天下方有小圆点；手机上左右滑动翻周 */
function WeekStrip({ monday, selected, onSelect, onPage }: { monday: string; selected: string; onSelect: (d: string) => void; onPage: (n: number) => void }) {
  const cal = useCalendar();
  const today = cal.today();
  const touch = useRef<{ x: number; y: number } | null>(null);
  // 翻周时的滑入方向（在渲染时算出，和 key={monday} 的重新挂载同一帧生效）
  const prevMonday = useRef(monday);
  const slideRef = useRef<"" | "from-right" | "from-left">("");
  if (prevMonday.current !== monday) {
    slideRef.current = monday > prevMonday.current ? "from-right" : "from-left";
    prevMonday.current = monday;
  }
  const slide = slideRef.current;

  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    touch.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const s = touch.current;
    const t = e.changedTouches[0];
    touch.current = null;
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) onPage(dx < 0 ? 1 : -1);
  };

  return (
    <div className={`sch-strip ${slide ? `is-${slide}` : ""}`} key={monday} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} role="tablist" aria-label="选择日期">
      {weekDates(cal, monday).map((d, i) => {
        const on = d === selected;
        return (
          <button
            key={d}
            type="button"
            role="tab"
            aria-selected={on}
            aria-label={`${cal.monthDay(d)} ${cal.weekdayName(d)}`}
            className={`sch-strip-day ${on ? "is-on" : ""} ${i >= 5 ? "is-weekend" : ""}`}
            onClick={() => onSelect(d)}
          >
            <span className="sch-strip-wd">{WEEKDAY_NAMES[i + 1]}</span>
            <span className="sch-strip-num kp-num">{dayNum(d)}</span>
            {d === today && !on && <i className="sch-today-dot" aria-label="今天" />}
          </button>
        );
      })}
    </div>
  );
}

/** 一条日程：类别色条 · 两行时间 · 标题和说明 · 提醒铃铛 / 完成勾。所有日程同一种样式，只按类别配色 */
function AgendaRow({ item, isNow }: { item: AgendaItem; isNow: boolean }) {
  const cal = useCalendar();
  const rules = useStore((s) => s.reminderRules);
  const event = useStore((s) => s.scheduleEvents[item.eventId]);
  const assignment = useStore((s) => (item.assignmentIds[0] ? s.assignments[item.assignmentIds[0]] : undefined));
  // 孩子点了"做完了"：待确认 / 已加分 / 不算
  const ckKey = checkinKeyOf(item, cal.dateString(item.startAt));
  const checkin = useStore((s) => (ckKey ? Object.values(s.checkins).find((k) => k.occurrenceKey === ckKey && !k.deletedAt) : undefined));
  const style = categoryStyle(item.category);
  const rule = ruleOf(rules, item.eventId);
  const [top, bottom] = timeLines(item);
  const wordTop = isAllDay(item) || (timeModeOf(item) === "range" && dayPart(item) !== "single");
  const learned = assignment != null && isDone(assignment);

  const parts: string[] = [style.name];
  const act = actionText(item);
  if (act) parts.push(act);
  const d = dayText(item);
  if (d) parts.push(d);
  if (event) parts.push(describeRepeat(parseRepeat(event.rrule, cal.weekday(event.startDate)), event.startDate));
  if (learned) parts.push("已完成");
  if (isNow) parts.push("进行中");
  if (rule) {
    if (rule.requireAck) parts.push("需确认");
    else {
      const first = [...rule.offsetsMin].sort((a, b) => a - b)[0];
      if (first != null) parts.push(offsetText(first));
    }
  }
  if (event?.untilDate) parts.push(`至${cal.monthDay(event.untilDate)}`);
  let meta = parts.join(" · ");
  if (checkin) meta = `${checkinText(checkin)} · ${meta}`;

  return (
    <div className={`sch-row ${isNow ? "is-now" : ""}`}>
      <i className="sch-row-bar" style={{ background: style.dot }} />
      <div className="sch-row-time">
        <span className={wordTop ? "is-word" : "kp-num"}>{top}</span>
        {bottom != null && <small className={dayPart(item) === "first" || bottom === "前" ? "" : "kp-num"}>{bottom}</small>}
      </div>
      <div className="sch-row-main">
        <span className="sch-row-title">{item.title}</span>
        <span className="sch-row-meta" title={meta}>
          {meta}
        </span>
      </div>
      {(learned || checkin?.status === "approved") && <CircleCheck size={18} color={C.success} strokeWidth={2.6} aria-label="已完成" />}
      {item.action?.type === "content" && <BookOpen size={16} color={C.inkSoft} aria-label="打开学习内容" />}
      {item.action?.type === "app" && <ExternalLink size={16} color={C.inkSoft} aria-label="打开其它 App" />}
      {rule && <Bell size={16} color={C.inkSoft} aria-label="有提醒" />}
      <ChevronRight size={16} color={C.inkSoft} strokeWidth={2.6} aria-hidden />
    </div>
  );
}

/** 完成确认的状态文字 */
function checkinText(k: Checkin): string {
  if (k.status === "pending") return `做完了，待确认 +${k.points + (k.bonusPoints ?? 0)}`;
  if (k.status === "approved") return `已确认 +${k.awardedPoints ?? 0}`;
  return "做完了（不算）";
}

// ---------- 周视图 ----------

export function WeekView({
  child,
  date,
  onSelect,
  onPickDay,
  onEdit,
}: {
  child: Child;
  date: string;
  onSelect: (d: string) => void;
  onPickDay: (d: string) => void;
  onEdit: OpenEditor;
}) {
  const cal = useCalendar();
  const monday = cal.mondayOf(date);
  const dates = weekDates(cal, monday);
  useAgendaRange(child.id, monday, dates[6]!);
  const days = useDays(child.id, dates);
  const today = cal.today();

  return (
    <div className="sch-week">
      <div className="sch-week-nav">
        <button type="button" className="kp-icon-btn" aria-label="上一周" title="上一周" onClick={() => onSelect(cal.addDays(date, -7))}>
          <ChevronLeft size={18} strokeWidth={2.6} />
        </button>
        <span className="sch-range">
          {cal.monthDay(monday)} – {cal.monthDay(dates[6]!)}
        </span>
        <button type="button" className="kp-icon-btn" aria-label="下一周" title="下一周" onClick={() => onSelect(cal.addDays(date, 7))}>
          <ChevronRight size={18} strokeWidth={2.6} />
        </button>
      </div>
      <div className="sch-week-days">
        {dates.map((d, i) => {
          const items = days[i]!;
          return (
            <section key={d} className="sch-week-day">
              <button type="button" className={`sch-week-head ${i >= 5 ? "is-weekend" : ""}`} onClick={() => onPickDay(d)}>
                <b>周{WEEKDAY_NAMES[i + 1]}</b>
                <span>{cal.monthDay(d)}</span>
                {d === today && (
                  <Pill tint={C.accent} size={11}>
                    今天
                  </Pill>
                )}
                <ChevronRight size={14} strokeWidth={2.6} className="sch-week-head-go" aria-hidden />
              </button>
              {items.length === 0 && <p className="sch-none">没有安排</p>}
              {items.map((it) => (
                <button key={agendaItemId(it)} type="button" className="sch-compact-wrap is-button" onClick={() => onEdit(it.eventId, d)}>
                  <CompactRow item={it} />
                </button>
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function CompactRow({ item }: { item: AgendaItem }) {
  const style = categoryStyle(item.category);
  const d = dayText(item);
  const wordy = isAllDay(item) || (timeModeOf(item) === "range" && dayPart(item) !== "single");
  const act = actionText(item);
  return (
    <div className="sch-compact">
      <span className="sch-compact-icon" style={{ background: style.tint, color: style.ink }}>
        <KPIcon name={item.icon} size={15} strokeWidth={2.4} />
      </span>
      <span className={`sch-compact-time ${wordy ? "is-word" : "kp-num"}`}>{timeText(item)}</span>
      <span className="sch-compact-title">{item.title}</span>
      {d && <small>{d}</small>}
      {act && <small>{act}</small>}
    </div>
  );
}

// ---------- 月视图 ----------

export function MonthView({ child, date, onSelect, onPickDay }: { child: Child; date: string; onSelect: (d: string) => void; onPickDay: (d: string) => void }) {
  const cal = useCalendar();
  const first = cal.monthStart(date);
  const count = cal.daysInMonth(first);
  const last = cal.addDays(first, count - 1);
  useAgendaRange(child.id, first, last);
  const dates = Array.from({ length: count }, (_, i) => cal.addDays(first, i));
  const days = useDays(child.id, dates);
  const leading = cal.weekday(first) - 1;
  const today = cal.today();

  return (
    <div className="sch-month">
      <div className="sch-week-nav">
        <button type="button" className="kp-icon-btn" aria-label="上个月" title="上个月" onClick={() => onSelect(cal.addMonths(date, -1))}>
          <ChevronLeft size={18} strokeWidth={2.6} />
        </button>
        <span className="sch-range">{cal.yearMonth(first)}</span>
        <button type="button" className="kp-icon-btn" aria-label="下个月" title="下个月" onClick={() => onSelect(cal.addMonths(date, 1))}>
          <ChevronRight size={18} strokeWidth={2.6} />
        </button>
      </div>
      <div className="sch-month-grid">
        {[1, 2, 3, 4, 5, 6, 7].map((w) => (
          <span key={w} className={`sch-month-wd ${w >= 6 ? "is-weekend" : ""}`}>
            {WEEKDAY_NAMES[w]}
          </span>
        ))}
        {Array.from({ length: leading }, (_, i) => (
          <span key={`blank${i}`} />
        ))}
        {dates.map((d, i) => (
          <MonthCell key={d} date={d} items={days[i]!} selected={d === date} today={d === today} onClick={() => onPickDay(d)} />
        ))}
      </div>
      <CategoryLegend />
    </div>
  );
}

/** 月视图一格：手机上是最多 4 条类别色条；桌面上列出全天 / 跨天日程的名称和其它日程的开始时间 */
function MonthCell({ date, items, selected, today, onClick }: { date: string; items: AgendaItem[]; selected: boolean; today: boolean; onClick: () => void }) {
  const cal = useCalendar();
  // 全天 / 跨天的放前面
  const sorted = [...items].sort((a, b) => Number(isAllDay(b) || isMultiDay(b)) - Number(isAllDay(a) || isMultiDay(a)));
  const shown = sorted.slice(0, 3);
  const more = sorted.length - shown.length;
  const label = `${cal.monthDay(date)} ${cal.weekdayName(date)}，${items.length ? `${items.length} 项安排` : "没有安排"}`;
  return (
    <button type="button" className={`sch-month-cell ${selected ? "is-on" : ""} ${today ? "is-today" : ""}`} onClick={onClick} aria-label={label} aria-pressed={selected}>
      <span className="sch-month-num kp-num">{dayNum(date)}</span>
      <span className="sch-month-bars" aria-hidden>
        {items.slice(0, 4).map((it) => (
          <i key={agendaItemId(it)} style={{ background: categoryStyle(it.category).dot }} />
        ))}
      </span>
      <span className="sch-month-items" aria-hidden>
        {shown.map((it) => {
          const s = categoryStyle(it.category);
          const span = isAllDay(it) || isMultiDay(it);
          return span ? (
            <span key={agendaItemId(it)} className="sch-month-span" style={{ background: s.tint, color: s.ink }}>
              {it.title}
            </span>
          ) : (
            <span key={agendaItemId(it)} className="sch-month-timed">
              <i style={{ background: s.dot }} />
              <span className="kp-num">{shortTimeText(it)}</span> {it.title}
            </span>
          );
        })}
        {more > 0 && <span className="sch-month-more">还有 {more} 项</span>}
      </span>
    </button>
  );
}
